/**
 * Partner landing page configured by database content
 */
import React, { Component } from 'react';
import graphql from 'babel-plugin-relay/macro';
import { QueryRenderer } from 'react-relay';
import { Redirect } from 'react-router-dom';
import AbEnvironment from '../auth/AbEnvironment';
import PropTypes from 'prop-types';
import withStyles from '@mui/styles/withStyles';
import { Grid, Paper} from '@mui/material';


import LoadingSpinner from '../ui/LoadingSpinner';
import ServerError from '../ui/ServerError';
import VendorPartnerProgramBlockHeader from './VendorPartnerProgramBlockHeader';
import VendorPartnerProgramBlockCta from './VendorPartnerProgramBlockCta';
import VendorPartnerProgramBlockAssets from './VendorPartnerProgramBlockAssets';
import VendorPartnerProgramBlockCommission from './VendorPartnerProgramBlockCommission';
import VendorPartnerProgramBlockImage from './VendorPartnerProgramBlockImage';
import VendorPartnerProgramBlockVideo from './VendorPartnerProgramBlockVideo';
import VendorPartnerProgramBlockApps from './VendorPartnerProgramBlockApps';

const abEnvironment = new AbEnvironment();

const VendorPartnerProgramContainerQuery = graphql`
query VendorPartnerProgramContainerQuery($vppContentBlockId: UUID!) {
  vppContentBlockTByVppContentBlockId(vppContentBlockId: $vppContentBlockId) {
    direction
    justifyContent
    alignItems
    colSpacing
    colSpacingXs
    colSpacingSm
    colSpacingMd
    rowSpacing
    rowSpacingXs
    rowSpacingSm
    rowSpacingMd
    spacing
    spacingXs
    spacingSm
    spacingMd
    columns
    columnsXs
    columnsSm
    columnsMd
    inPaper
    vppContentBlockTsByParentBlockId(orderBy: BLOCK_ORDER_ASC) {
      totalCount
      nodes {
        vppContentBlockId
        blockType
        blockOrder
        itemXs
        itemSm
        itemMd
        inPaper
      }
    }
  }
}
`;

const styleSheet = theme => ({
  paper: {
    textAlign: 'left',
    paddingTop: 16,
    paddingLeft: 20,
    paddingRight: 20,
    paddingBottom: 20
  }
});

class VendorPartnerProgramContainer extends Component {

  render() {
    const { isAuthenticated, isAuthenticatedVendor, isAuthenticatedReseller, isAuthenticatedCustomer, isAuthenticatedAppBind, isUnauthenticatedCustomer, needAssent, logError } = this.props.auth;
    const classes = this.props.classes;

    if (!isAuthenticated() || needAssent()) { 
      return ( <Redirect to='/home' /> );
    }    
    if (!isAuthenticatedVendor() && !isAuthenticatedCustomer() && !isUnauthenticatedCustomer() && !isAuthenticatedReseller() && !isAuthenticatedAppBind()) {
      // others cannot see this component
      return ( <Redirect to='/home' /> );
    } 

    return (
        <QueryRenderer
          environment={abEnvironment.getEnvironment()}
          query={VendorPartnerProgramContainerQuery}
          variables={{vppContentBlockId: this.props.containerBlockId}}
          render={({error, props})=> {
            if (error) {
              logError(error.message);
              return (
                <ServerError/>
              );
            } else if (props) {
              // TODO - figure out the spacing
              let spacing = props.vppContentBlockTByVppContentBlockId.spacing;
              let rowSpacing = props.vppContentBlockTByVppContentBlockId.rowSpacing;
              if (rowSpacing == null) {
                rowSpacing = spacing;
              }
              let columnSpacing = props.vppContentBlockTByVppContentBlockId.columnSpacing;
              if (columnSpacing == null) {
                columnSpacing = spacing;
              }

              let direction = props.vppContentBlockTByVppContentBlockId.direction;
              if (direction !== "row" && direction !== "row-reverse" && direction !== "column" && direction !== "column-reverse") {
                direction = "row";
              }

              let justifyContent = props.vppContentBlockTByVppContentBlockId.justifyContent;
              if (justifyContent !== "flex-start" && justifyContent !== "center" && justifyContent !== "flex-end" && justifyContent !== "space-between" && justifyContent !== "space-around" && justifyContent !== "space-evenly") {
                justifyContent = "flex-start";
              }

              let alignItems = props.vppContentBlockTByVppContentBlockId.alignItems;
              if (alignItems !== "flex-start" && alignItems !== "center" && alignItems !== "flex-end" && alignItems !== "stretch" && alignItems !== "baseline") {
                alignItems = "flex-start";
              }

              // Content blocks are already ordered in the query
              // vppContentBlockTByVppContentBlockId.vppContentBlockTsByParentBlockId.nodes
              let contentBlocks = props.vppContentBlockTByVppContentBlockId.vppContentBlockTsByParentBlockId.nodes;

              // Counter for unique keys
              let counter = 0;
              return (
                // The container element is a Grid container that wraps child blocks
                <Grid container rowSpacing={rowSpacing} columnSpacing={columnSpacing} direction={direction} justifyContent={justifyContent} alignItems={alignItems}>

                    { contentBlocks.map(content => {                  
                      return (
                        <Grid item xs={content.itemXs} key={counter++}>
                          {content.blockType === 0 && content.inPaper === 1 &&
                          <Paper className={classes.paper}>
                            <VendorPartnerProgramContainer devMode={this.props.devMode} vpp={this.props.vpp}  formPresent={this.props.formPresent} relationship={this.props.relationship} assets={this.props.assets} containerBlockId={content.vppContentBlockId} showProgramForm={this.props.showProgramForm} auth={this.props.auth} classes={this.props.classes}/>
                          </Paper>}
                          {content.blockType === 0 && content.inPaper === 0 &&
                          <VendorPartnerProgramContainer devMode={this.props.devMode} vpp={this.props.vpp}  formPresent={this.props.formPresent} relationship={this.props.relationship} assets={this.props.assets} containerBlockId={content.vppContentBlockId} showProgramForm={this.props.showProgramForm} auth={this.props.auth} classes={this.props.classes}/>}

                          {content.blockType === 1 &&
                          <Paper className={classes.paper}>
                            <VendorPartnerProgramBlockHeader vpp={this.props.vpp} vppRelationship={this.props.relationship} auth={this.props.auth}/>
                          </Paper>}

                          {content.blockType === 2 &&
                          <Paper className={classes.paper}>
                            <VendorPartnerProgramBlockCta devMode={this.props.devMode} vpp={this.props.vpp} formPresent={this.props.formPresent} vppBlock={content} vppRelationship={this.props.relationship} showProgramForm={this.props.showProgramForm} auth={this.props.auth}/>
                          </Paper>}

                          {content.blockType === 3 && alignItems !== "stretch" &&
                          <Paper className={classes.paper}>
                            <VendorPartnerProgramBlockAssets vpp={this.props.vpp} vppBlock={content} vppRelationship={this.props.relationship} assets={this.props.assets} auth={this.props.auth}/>
                          </Paper>}
                          {content.blockType === 3 && alignItems === "stretch" &&
                          <Paper className={classes.paper} style={{height: 'calc(100% - 36px)'}}>
                            <VendorPartnerProgramBlockAssets vpp={this.props.vpp} vppBlock={content} vppRelationship={this.props.relationship} assets={this.props.assets} auth={this.props.auth}/>
                          </Paper>}

                          {content.blockType === 4 &&
                          <Paper className={classes.paper}>
                            <VendorPartnerProgramBlockCommission devMode={this.props.devMode} vpp={this.props.vpp} vppBlock={content} vppRelationship={this.props.relationship} auth={this.props.auth}/>
                          </Paper>}

                          {content.blockType === 5 &&
                          <Paper className={classes.paper}>
                            <VendorPartnerProgramBlockImage vpp={this.props.vpp} vppBlock={content} vppRelationship={this.props.relationship}  assets={this.props.assets}  auth={this.props.auth}/>
                          </Paper>}

                          {content.blockType === 6 &&
                          <Paper className={classes.paper}>
                            <VendorPartnerProgramBlockVideo vpp={this.props.vpp} vppBlock={content} vppRelationship={this.props.relationship}  assets={this.props.assets} auth={this.props.auth}/>
                          </Paper>}                         

                          {content.blockType === 7 &&
                          <Paper className={classes.paper}>
                            <VendorPartnerProgramBlockApps devMode={this.props.devMode} vpp={this.props.vpp} vppBlock={content} vppRelationship={this.props.relationship}  assets={this.props.assets} auth={this.props.auth} history={this.props.history}/>
                          </Paper>}                         
                        </Grid>
                      );
                    })}
                </Grid>
                );

            } else {
              return (
                <LoadingSpinner />
              );
            }
          }}
        />
    );
  }
}

VendorPartnerProgramContainer.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styleSheet)(VendorPartnerProgramContainer);


/*
{content.blockType === 3 && alignItems !== "stretch" &&
<Paper className={classes.paper}>
  <VendorPartnerProgramBlockAssets vpp={this.props.vpp} vppBlock={content} vppRelationship={this.props.relationship} assets={this.props.assets} auth={this.props.auth}/>
</Paper>}
{content.blockType === 3 && alignItems === "stretch" &&
<Grid container spacing={0}>
  <Grid item xs={12}>
    <Paper className={classes.stretchPaper}>
      <VendorPartnerProgramBlockAssets vpp={this.props.vpp} vppBlock={content} vppRelationship={this.props.relationship} assets={this.props.assets} auth={this.props.auth}/>
    </Paper>
  </Grid>
</Grid>}
*/

/*
                          {content.blockType === 3 &&
                          <Paper className={classes.paper}>
                            <VendorPartnerProgramBlockAssets vpp={this.props.vpp} vppBlock={content} vppRelationship={this.props.relationship} assets={this.props.assets} auth={this.props.auth}/>
                          </Paper>}


*/